import {
  AfterContentInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import {
  AreaTypeEnum,
  CommissioningStatusEnum,
  SupplyAppointmentPartDayStatusEnum,
  SupplyAppointmentStatusEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { MetaService } from '@bo-client/core/api/meta-service';
import { AppointmentAgreeModalComponent } from '@bo-client/activation-management/commissioning/forms/components/appointment-agree-modal/appointment-agree-modal.component';
import { Moment } from 'moment';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-supply-date',
  templateUrl: './supply-appointment.component.html',
  styleUrls: ['./supply-appointment.component.scss']
})
export class SupplyAppointmentComponent implements OnInit, AfterContentInit, OnDestroy {
  private compUnsubs$ = new Subject<void>();
  @Input() inputForm: FormGroup;
  @Input() firstDateAvailable: Date;
  @Input() selectedWishDateOK: boolean;
  @Input() selectedWishPartDateOK: boolean;
  @Input() isAdminUser: boolean;
  @Input() commissioningOrderDto: CommissioningRequestDtoModel;
  @Input() canUpdate = false;
  @Input() areaType: AreaTypeEnum;
  /**
   * Überspringt die spartenbezogene Konfiguration „Durchführungstermin deaktivieren". Zu setzen für
   * Formulare, die über eine eigene Konfiguration entscheiden, ob der Wunschtermin erfragt wird —
   * etwa die externe Befundprüfung, die spartenübergreifend beantragt werden kann.
   */
  @Input() ignoreAreaWishDateConfig = false;
  @Output() acceptAppoinment = new EventEmitter<any>();

  agreed = SupplyAppointmentStatusEnum.Agreed;
  pending = SupplyAppointmentStatusEnum.Pending;
  pendingAppointment = CommissioningStatusEnum.PendingAppointment;
  statusApproved = CommissioningStatusEnum.Approved;
  requestedApproved = [
    CommissioningStatusEnum.Requested.toString(),
    CommissioningStatusEnum.Commissioned.toString(),
    CommissioningStatusEnum.NAVSent.toString()
  ];

  listPartDays: Array<any> = this.metaService.getSupplyAppointmentPartDayStatus();

  today: Date = new Date();

  deactivateSupplyWishDate: Observable<boolean>;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private matDialog: MatDialog,
    private metaService: MetaService,
    private dataShareService: DataShareService
  ) {}

  ngOnInit(): void {
    if (this.ignoreAreaWishDateConfig) {
      this.deactivateSupplyWishDate = of(false);
      this.detectFormChanges();
      return;
    }

    const configKeyMap = {
      [AreaTypeEnum.GAS]: 'commissioning.gasConfig.deactivateSupplyWishDate',
      [AreaTypeEnum.POWER]: 'commissioning.powerConfig.deactivateSupplyWishDate',
      [AreaTypeEnum.WATER]: 'commissioning.waterConfig.deactivateSupplyWishDate',
      [AreaTypeEnum.DISTRICTHEAT]: 'commissioning.districtHeatConfig.deactivateSupplyWishDate',
      [AreaTypeEnum.HEAT]: 'commissioning.districtHeatConfig.deactivateSupplyWishDate'
    };

    const configKey = configKeyMap[this.areaType] || 'commissioning.powerConfig.deactivateSupplyWishDate';

    this.deactivateSupplyWishDate = this.dataShareService
      .getSingleTenantConfig$<boolean>(configKey, false)
      .pipe(takeUntilDestroyed(this.destroyRef));
    this.deactivateSupplyWishDate.subscribe((disable) => {
      if (!this.isAdminUser && disable) {
        this.inputForm.get('supplyAppointment').disable();
        this.inputForm.get('supplyAppointmentWishDate').disable();
        this.inputForm.get('supplyAppointmentWishPartday').disable();
      } else {
        this.inputForm.get('supplyAppointment').enable();
        this.inputForm.get('supplyAppointmentWishDate').enable();
        this.inputForm.get('supplyAppointmentWishPartday').enable();
      }
    });
    this.detectFormChanges();
    // this.loadListStatus(this.checkIfFriday());
  }

  checkIfFriday(): boolean {
    if (
      this.inputForm &&
      this.inputForm.get('supplyAppointment') &&
      this.inputForm.get('supplyAppointment').value
    ) {
      return new Date(this.inputForm.get('supplyAppointmentWishDate').value).getDay() == 5;
    } else {
      return false;
    }
  }

  loadListStatus(isFriday: boolean) {
    if (isFriday) {
      this.listPartDays = this.metaService
        .getSupplyAppointmentPartDayStatus()
        .filter((ele) => ele.value != SupplyAppointmentPartDayStatusEnum.Afternoon);
    } else {
      this.listPartDays = this.metaService.getSupplyAppointmentPartDayStatus();
    }
  }

  detectFormChanges() {
    this.appointmentForm.valueChanges.pipe(takeUntil(this.compUnsubs$)).subscribe(() => {
      this.loadListStatus(this.checkIfFriday());
    });
  }

  weekendsDatesFilter = (d: Date | Moment): boolean => {
    if (!d) return false;

    let day = 0;
    if ('day' in d) {
      day = d.day();
    } else {
      day = new Date(d).getDay();
    }

    /* Prevent Saturday and Sunday for select. */
    return day !== 0 && day !== 6;
  };

  ngAfterContentInit() {
    if (!this.inputForm.get('supplyAppointmentWishDate').value) {
      this.inputForm.get('supplyAppointmentWishDate').patchValue(this.firstDateAvailable);
    }
  }

  dialogAppoinmentAgree() {
    this.inputForm.get('date').enable();
    this.inputForm.get('time').enable();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'supply-appointment';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      inputForm: this.inputForm,
      isAdminUser: this.isAdminUser,
      commissioningOrderDto: this.commissioningOrderDto
    };
    this.matDialog
      .open(AppointmentAgreeModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((resOk) => {
        if (resOk) {
          this.acceptAppoinment.emit(true);
        } else {
          if (!this.inputForm.get('date').value || !this.inputForm.get('time').value) {
            this.inputForm.get('date').patchValue(null);
            this.inputForm.get('time').patchValue(null);
          }
        }
      });
  }

  get appointmentForm() {
    return this.inputForm;
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
